import type { ChangeEvent, Dispatch, RefObject, SetStateAction } from "react";

import {
  IconChatAttention,
  IconDeleteForever,
  IconDownload,
  IconEdit,
  IconFileAdd,
  IconFileInfo,
  IconFullscreen,
  IconGhost,
  IconSelectOnModel,
  IconViewer,
} from "@synthesis/shared-icons";
import { getApiBaseUrl } from "@synthesis/shared-services";
import { saveModelIdsAtRedis } from "@synthesis/shared-services/viewer/modelIdCacheController/client";
import { SearchBar, Toolbar, type ToolbarContextValue } from "@synthesis/shared-ui";
import { logger } from "@synthesis/shared-utils";

import type { FileMetaDto } from "../api";
import { useModelGantt } from "../components/files/hooks";
import { ModelButton } from "../components/toolbar/local-components";
import { useUploadProgress } from "../file-uploader/file-uploader";
import { useStores } from "../store";
import { InformationButton, MarkupButton, ViewChangeDropdown, DownloadButton } from "./local-component";
import SortDropdown from "./local-component/sort-dropdown";

type IntegrationModalState = {
  isOpen: boolean;
  addFile: boolean;
  rename: boolean;
  delete: boolean;
  updateVersion: boolean;
};

const useToolbarActions = (
  option: ToolbarContextValue["option"],
  _disabledRight: boolean | undefined,
  action: ToolbarContextValue["action"],
  setSelectedFile: Dispatch<SetStateAction<FileMetaDto | undefined>>,
  setIsOpenRename: Dispatch<SetStateAction<boolean>>,
  inputRef: RefObject<HTMLInputElement | null>,
  setIsOpenDeleteFile: Dispatch<SetStateAction<boolean>>,
  isIntegration: boolean | undefined,
  setIsModalIntegration: Dispatch<SetStateAction<IntegrationModalState>>,
  setSelectedPdfFile: Dispatch<SetStateAction<FileMetaDto | undefined>>,
  setIsOpenPdfModal: Dispatch<SetStateAction<boolean>>,
  setIsOpenMeta: Dispatch<SetStateAction<boolean>>,
  setMetaFile: Dispatch<SetStateAction<FileMetaDto | null>>,
  setPreviewModels: Dispatch<SetStateAction<string>>,
  setIsMarkupOpen: Dispatch<SetStateAction<boolean>>,
  setMarkupFile: Dispatch<SetStateAction<FileMetaDto | undefined>>,
) => {
  const { fileStore, generalStore } = useStores();
  const { patchModelIdFromGantt } = useModelGantt(generalStore.projectId ?? "");
  const { versionUpdate } = useUploadProgress();

  const handleDownload = async () => {
    if (generalStore.checkedFiles.size === 1) {
      const currentFile = fileStore.files.find((file) => file.id === Array.from(generalStore.checkedFiles.keys())[0]);
      if (!currentFile) {
        return;
      }
      await fileStore.downloadFile({
        id: currentFile.id,
        baseName: currentFile.baseName || "Безымянный",
        ext: currentFile.ext || "ifc",
      });
      return;
    }
    await fileStore.downloadArchiveFile();
  };
  const closeModalDeleteFile = () => {
    setIsOpenDeleteFile(false);
    setSelectedFile(undefined);
  };

  const uploadFileRequest = async (event: ChangeEvent<HTMLInputElement>) => {
    const checkedFileIds = Array.from(generalStore.checkedFiles.values()).map((file) => file.id);
    const matchingFiles = fileStore.files.filter((file) => checkedFileIds.includes(file.id));
    const matchingFile = matchingFiles[0];

    if (!matchingFile) {
      return;
    }

    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      generalStore.setState("loading");
      versionUpdate(matchingFile, file);

      generalStore.setCheckedContents(matchingFile);
      generalStore.setState("success");
    } catch (e) {
      logger.error("error at upload file >>>", e);
    }
  };

  const handleSearchFiles = (value: string) => {
    fileStore.setSearchFiles(value);
  };
  const handleUpload = () => {
    if (isIntegration) {
      setIsModalIntegration((prevState) => ({
        ...prevState,
        updateVersion: true,
        isOpen: true,
      }));
    } else {
      inputRef.current?.click();
    }
  };
  const handleOpenViewer = (isAssembly: boolean) => {
    if (isAssembly && generalStore.checkedFiles.size < 2) return;
    if (!isAssembly && generalStore.checkedFiles.size !== 1) return;
    const models = Array.from(generalStore.checkedFiles.values())
      .map((e) => e.id)
      .join(",");
    if (!models) return;
    void (async () => {
      const modelsId: string[] = models.split(",");
      const generateModelId = (await saveModelIdsAtRedis({ body: { modelIds: modelsId } })).data;
      const url = `${getApiBaseUrl()}/viewer/#/${generalStore.projectId}?models=${generateModelId}`;
      window.open(url, "_blank")?.focus();
    })();
  };

  const handleOpenInfo = () => {
    if (generalStore.checkedFiles.size !== 1) return;
    const checkedFileIds = Array.from(generalStore.checkedFiles.values()).map((file) => file.id);
    const matchingFiles = fileStore.files.filter((file) => checkedFileIds.includes(file.id));
    setMetaFile(matchingFiles[0] ?? null);
    setIsOpenMeta(true);
    const models = Array.from(generalStore.checkedFiles.values())
      .map((e) => e.id)
      .join(",");
    setPreviewModels(models);
  };

  const handleOpenMarkup = () => {
    if (generalStore.checkedFiles.size !== 1) return;
    const checkedFileIds = Array.from(generalStore.checkedFiles.values()).map((file) => file.id);
    const matchingFiles = fileStore.files.filter((file) => checkedFileIds.includes(file.id));
    setMarkupFile(matchingFiles[0]);
    setIsMarkupOpen(true);
  };

  const handleAddLeft = () => {
    const selectedFile = () => {
      const checkedFileIds = Array.from(generalStore.checkedFiles.values()).map((file) => file.id);
      const matchingFiles = fileStore.visibleFiles.filter((file) => checkedFileIds.includes(file.id));
      setSelectedFile(matchingFiles[0]);
    };
    const handleDelete = () => {
      selectedFile();
      if (isIntegration) {
        setIsModalIntegration((prevState) => ({
          ...prevState,
          delete: true,
          isOpen: true,
        }));
      } else {
        setIsOpenDeleteFile(true);
      }
    };
    const handleRename = () => {
      selectedFile();
      if (isIntegration) {
        setIsModalIntegration((prevState) => ({
          ...prevState,
          rename: true,
          isOpen: true,
        }));
      } else {
        setIsOpenRename(true);
      }
    };

    if (generalStore.activeTab === "drawings") {
      option?.clean("left");
      return;
    }

    if (generalStore.activeTab === "docs") {
      const openDoc = () => {
        const checkedFileIds = Array.from(generalStore.checkedFiles.values()).map((file) => file.id);
        const matchingFiles = fileStore.visibleFiles.filter((file) => checkedFileIds.includes(file.id));
        const firstFile = matchingFiles[0];

        if (matchingFiles.length === 1 && firstFile && firstFile.ext?.toLowerCase() === "pdf") {
          setSelectedPdfFile(firstFile);
          setIsOpenPdfModal(true);
          return;
        }

        if (matchingFiles.length === 1 && firstFile) {
          fileStore.downloadFile({
            id: firstFile.id,
            baseName: firstFile.baseName || "Безымянный",
            ext: firstFile.ext ?? "",
          });
        } else if (matchingFiles.length > 1 && firstFile) {
          fileStore.downloadArchiveFile({
            id: firstFile.id,
            baseName: firstFile.baseName || "Безымянный",
            ext: firstFile.ext ?? "",
          });
        }
      };

      const renameDocButton = <Toolbar.Element optionKey={"docs:rename"} icon={IconEdit} label="Переименовать" />;
      const downloadDocElement = (
        <Toolbar.Element asChild optionKey={"docs:download"} icon={IconDownload} label="Скачать">
          <DownloadButton />
        </Toolbar.Element>
      );
      const deleteDocElement = <Toolbar.Element optionKey={"docs:delete"} icon={IconDeleteForever} label="Удалить" />;
      const openDocElement = (
        <Toolbar.Element theme="secondary" optionKey={"docs:open"} icon={IconSelectOnModel} label="Открыть" buttonLabel="Открыть" />
      );

      option?.clean("left");
      option?.add("left", [
        openDocElement,
        <Toolbar.Divider key="docs:divider-1" optionKey="docs:divider-1" />,
        renameDocButton,
        downloadDocElement,
        <Toolbar.Divider key="docs:divider-2" optionKey="docs:divider-2" />,
        deleteDocElement,
      ]);
      action?.add({
        "docs:rename": handleRename,
        "docs:delete": handleDelete,
        "docs:download": handleDownload,
        "docs:open": openDoc,
      });
      return;
    }

    const modelButton = (
      <Toolbar.Element
        asChild
        optionKey="filesystem:model-open"
        icon={IconFullscreen}
        label="Открыть отдельно"
        action={() => handleOpenViewer(false)}
      >
        <ModelButton />
      </Toolbar.Element>
    );

    const modelButtonAssembly = (
      <Toolbar.Element
        asChild
        optionKey="filesystem:model-open-assembly"
        icon={IconViewer}
        label="Открыть сборку"
        action={() => handleOpenViewer(true)}
      >
        <ModelButton isAssembly />
      </Toolbar.Element>
    );

    const informationButton = (
      <Toolbar.Element asChild optionKey="filesystem:file-info" icon={IconFileInfo} label="Свойства и версии" action={handleOpenInfo}>
        <InformationButton />
      </Toolbar.Element>
    );

    const markupButton = (
      <Toolbar.Element asChild optionKey="filesystem:markup" icon={IconChatAttention} label="Замечания" action={handleOpenMarkup}>
        <MarkupButton />
      </Toolbar.Element>
    );

    const renameButton = <Toolbar.Element optionKey={"renameKey"} icon={IconEdit} label="Переименовать" />;

    const downloadElement = (
      <Toolbar.Element asChild optionKey={"download"} icon={IconDownload} label="Скачать">
        <DownloadButton />
      </Toolbar.Element>
    );

    const uploadVersionElement = <Toolbar.Element optionKey={"uploadVersionKey"} icon={IconFileAdd} label="Загрузить версию" />;

    const deleteElement = <Toolbar.Element optionKey={"deleteKey"} icon={IconDeleteForever} label="Удалить" />;

    option?.clean("left");
    option?.add("left", [
      modelButton,
      modelButtonAssembly,
      <Toolbar.Divider key="filesystem:divider-1" optionKey="filesystem:divider-1" />,
      renameButton,
      downloadElement,
      <Toolbar.Divider key="filesystem:divider-2" optionKey="filesystem:divider-2" />,
      markupButton,
      <Toolbar.Divider key="filesystem:divider-3" optionKey="filesystem:divider-3" />,
      uploadVersionElement,
      informationButton,
      <Toolbar.Divider key="filesystem:divider-4" optionKey="filesystem:divider-4" />,
      deleteElement,
    ]);
    action?.add({
      "filesystem:model-open": () => handleOpenViewer(false),
      "filesystem:model-open-assembly": () => handleOpenViewer(true),
      "filesystem:file-info": handleOpenInfo,
      "filesystem:markup": handleOpenMarkup,
      renameKey: handleRename,
      deleteKey: handleDelete,
      download: handleDownload,
      uploadVersionKey: handleUpload,
    });
  };

  const handleAddRight = () => {
    if (generalStore.activeTab === "drawings") {
      option?.clean("right");
      return;
    }

    const sortElement = (
      <Toolbar.Element asChild optionKey="filesystem:sort" icon={IconGhost} label="sort">
        <SortDropdown />
      </Toolbar.Element>
    );

    const viewChangeElement = (
      <Toolbar.Element asChild optionKey="filesystem:view-change" icon={IconGhost} label="viewChange">
        <ViewChangeDropdown />
      </Toolbar.Element>
    );

    const SearchBarElement = (
      <Toolbar.Element asChild optionKey="filesystem:search" icon={IconGhost} label="search">
        <SearchBar size="sm" className="min-w-[200px]" clearable onChange={handleSearchFiles} />
      </Toolbar.Element>
    );

    option?.clean("right");
    option?.add("right", [sortElement, viewChangeElement, SearchBarElement]);
  };

  return {
    handleAddLeft,
    handleAddRight,
    uploadFileRequest,
    closeModalDeleteFile,
    patchModelIdFromGantt,
  };
};

export default useToolbarActions;
